import { timing } from "../config";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ApiError, type MakerChain } from "../lib/api";
import { explorerName } from "../lib/explorer";
import { otherLabels, useNetwork } from "../lib/network";

/**
 * The small pieces every page of the Maker portal shares: the card, the pills,
 * the who-changes tag, the copy button, the explorer link, and the two ways a
 * page says it could not read.
 */

export type Tone = "ok" | "warn" | "grey" | "bad" | "plain";

/** A rounded status pill: "Quoting", "Not eligible", "Waiting for AileFX". */
export function Pill({ tone, children, busy = false }: { tone: Tone; children: ReactNode; busy?: boolean }) {
  return (
    <span className={`mk-pill mk-pill-${tone}`} role={busy ? "status" : undefined}>
      {busy && <Spinner />}
      {children}
    </span>
  );
}

export function Spinner() {
  return (
    <svg className="mk-spin" width="12" height="12" viewBox="0 0 12 12" fill="none" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeOpacity="0.3" />
      <path d="M6 1.5a4.5 4.5 0 0 1 4.5 4.5" stroke="currentColor" />
    </svg>
  );
}

export type Who = "owner" | "treasury" | "operators" | "operators-until-deployment";

const WHO_LABEL: Record<Who, string> = {
  owner: "Owner wallet",
  treasury: "Treasury wallet",
  operators: "Any operator",
  "operators-until-deployment": "Any operator, until deployment",
};

/** Who changes a card's values. */
export function WhoTag({ who }: { who: Who }) {
  return (
    <span className="mk-tag">
      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {who === "owner" && <path d="M2 4h9.5v7.5H2zM2 4l7-2v2M9 7.5h2.5" />}
        {who === "treasury" && (
          <>
            <rect x="1.5" y="2.5" width="11" height="9" rx="1.5" />
            <circle cx="7" cy="7" r="2" />
            <path d="M7 5v-.5M3.5 11.5v1M10.5 11.5v1" />
          </>
        )}
        {(who === "operators" || who === "operators-until-deployment") && (
          <>
            <circle cx="5" cy="4.5" r="2" />
            <path d="M1.5 12c0-2 1.6-3.5 3.5-3.5S8.5 10 8.5 12M9.5 2.8a2 2 0 0 1 0 3.4M10.5 8.7c1.2.4 2 1.6 2 3.3" />
          </>
        )}
      </svg>
      {WHO_LABEL[who]}
    </span>
  );
}

/** A white card with a heading. */
export function Card({
  title,
  tools,
  tag,
  desc,
  children,
  className,
  id: anchor,
}: {
  title: ReactNode;
  tools?: ReactNode;
  tag?: ReactNode;
  /** One line under the title saying what the card holds. */
  desc?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  const id = useId();
  return (
    <section id={anchor} className={className === undefined ? "mk-card" : `mk-card ${className}`} aria-labelledby={id}>
      <div className="mk-card-head">
        <div className="mk-card-title-line">
          <h2 id={id} className="mk-card-title">
            {title}
          </h2>
          {tag}
        </div>
        {tools !== undefined && <div className="mk-card-tools">{tools}</div>}
      </div>
      {desc !== undefined && <p className="mk-card-desc">{desc}</p>}
      {children}
    </section>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The 32px copy button beside an address. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current);
  }, []);
  return (
    <button
      type="button"
      className={done ? "mk-copy mk-copy-done" : "mk-copy"}
      aria-label={done ? "Copied" : label}
      title={done ? "Copied" : label}
      onClick={() => {
        void copyText(text).then((ok) => {
          if (!ok) return;
          setDone(true);
          if (timer.current !== null) clearTimeout(timer.current);
          timer.current = setTimeout(() => setDone(false), timing.ui.copiedMs);
        });
      }}
    >
      {done ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2.5 6.5l2.2 2.2 4.8-5" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
          <rect x="4" y="4" width="6.5" height="6.5" rx="1.4" />
          <path d="M8 4V2.9A1.4 1.4 0 0 0 6.6 1.5H2.9A1.4 1.4 0 0 0 1.5 2.9v3.7A1.4 1.4 0 0 0 2.9 8H4" />
        </svg>
      )}
    </button>
  );
}

/** "Basescan ↗" for a link, nothing without one. */
export function ExplorerLink({ href, label }: { href: string | null; label: string }) {
  if (href === null) return null;
  return (
    <a className="mk-ext" href={href} target="_blank" rel="noreferrer noopener" aria-label={`${label} (opens in a new tab)`}>
      {explorerName(href)} ↗
    </a>
  );
}

/** The network's explorer name ("Basescan"), for copy that names it before a link exists. */
export function scannerOf(chain: MakerChain): string {
  return explorerName(chain.explorerUrl);
}

/** A call failed because the network could not be read (503) or nothing answered at all. */
export function isChainDown(err: unknown): boolean {
  return err instanceof ApiError && (err.status === undefined || err.status === 503 || err.status === 502);
}

const RETRY_MS = timing.ui.chainDownRetryMs;

/**
 * A network whose reads fail: no stale values and no Set, while the other
 * networks keep working. Tries again by itself every 30 s.
 */
export function ChainDown({ chain, onRetry }: { chain: MakerChain; onRetry: () => void }) {
  const { chains, select } = useNetwork();
  const other = chains.find((c) => c.key !== chain.key) ?? null;
  const others = otherLabels(chains, chain);
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;
  useEffect(() => {
    const timer = setInterval(() => retryRef.current(), RETRY_MS);
    return () => clearInterval(timer);
  }, [chain.key]);
  return (
    <Card title={`${chain.label} is not answering`} tag={<Pill tone="warn">Unavailable</Pill>}>
      <p className="mk-text">
        Nothing on {chain.label} can be read right now, so nothing on {chain.label} can be changed either.
        {others !== "" && ` ${others} ${others.includes(" and ") ? "work" : "works"} as usual.`} This page tries again by itself.
      </p>
      <div className="mk-actions">
        <button type="button" className="mk-btn" onClick={onRetry}>
          Try again
        </button>
        {other !== null && (
          <button type="button" className="mk-btn" onClick={() => select(other.key)}>
            Switch to {other.label}
          </button>
        )}
      </div>
    </Card>
  );
}

/** Any other failure to read a page: what happened, in plain words, and Try again. */
export function LoadFailed({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <Card title="This page could not be read">
      <p className="mk-text" role="alert">
        {text}
      </p>
      <div className="mk-actions">
        <button type="button" className="mk-btn" onClick={onRetry}>
          Try again
        </button>
      </div>
    </Card>
  );
}

/** The two failure cards, chosen from the error; null while there is none. */
export function LoadError({ error, chain, onRetry }: { error: unknown; chain: MakerChain; onRetry: () => void }) {
  if (error === null || error === undefined) return null;
  if (isChainDown(error)) return <ChainDown chain={chain} onRetry={onRetry} />;
  const text =
    error instanceof ApiError && error.status === 404
      ? `${chain.label} is not served by the Maker portal.`
      : "The Maker portal did not answer as expected. Try again in a moment.";
  return <LoadFailed text={text} onRetry={onRetry} />;
}

/** A page's loading placeholder: nothing guessed, only the spinner. */
export function Loading() {
  return (
    <div className="mk-loading" role="status" aria-label="Loading">
      <span className="spinner" />
    </div>
  );
}
